import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  generateReportQueries,
  executeVisibilityReport,
  type ReportCategory,
  type ReportResultItem,
} from '@/api/adminService';
import { generateChatGptReport } from '@/components/report/generateChatGptReport';
import chatgptLogo from '@/assets/llm-logos/chatgpt.png';

interface Props {
  apiKey: string;
}

type Step = 'input' | 'edit' | 'running' | 'results';

export default function AdminReportsTab({ apiKey }: Props) {
  const [step, setStep] = useState<Step>('input');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [domain, setDomain] = useState('');
  const [categories, setCategories] = useState<ReportCategory[]>([]);
  const [results, setResults] = useState<ReportResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [expandedQueries, setExpandedQueries] = useState<Set<number>>(new Set());

  const handleGenerateQueries = async () => {
    if (!websiteUrl.trim()) return;
    setLoading(true);
    setError('');
    try {
      const data = await generateReportQueries(apiKey, websiteUrl.trim());
      setBusinessName(data.businessName);
      setDomain(data.domain);
      setCategories(data.categories);
      setStep('edit');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to generate queries');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuery = (catIdx: number, queryIdx: number, value: string) => {
    setCategories((prev) => {
      const next = [...prev];
      next[catIdx] = {
        ...next[catIdx],
        queries: next[catIdx].queries.map((q, i) =>
          i === queryIdx ? { ...q, query: value } : q,
        ),
      };
      return next;
    });
  };

  const handleRemoveQuery = (catIdx: number, queryIdx: number) => {
    setCategories((prev) => {
      const next = [...prev];
      next[catIdx] = {
        ...next[catIdx],
        queries: next[catIdx].queries.filter((_, i) => i !== queryIdx),
      };
      return next;
    });
  };

  const handleAddQuery = (catIdx: number) => {
    setCategories((prev) => {
      const next = [...prev];
      next[catIdx] = {
        ...next[catIdx],
        queries: [...next[catIdx].queries, { query: '', explanation: 'Custom query' }],
      };
      return next;
    });
  };

  const handleRunReport = async () => {
    setStep('running');
    setError('');
    setProgress(0);

    const allQueries = categories.flatMap((cat) =>
      cat.queries
        .filter((q) => q.query.trim())
        .map((q) => ({ category: cat.name, query: q.query })),
    );

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((p) => Math.min(p + 100 / allQueries.length / 3, 95));
      }, 1000);

      const data = await executeVisibilityReport(apiKey, websiteUrl, businessName, allQueries);
      clearInterval(progressInterval);
      setProgress(100);
      setResults(data.results);
      // Expand all by default
      setExpandedQueries(new Set(data.results.map((_, i) => i)));
      setStep('results');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to execute report');
      setStep('edit');
    }
  };

  const handleDownloadPdf = async () => {
    const doc = await generateChatGptReport({
      domain,
      businessName,
      results,
    });
    doc.save(`chatgpt-visibility-report-${domain}.pdf`);
  };

  const toggleExpanded = (idx: number) => {
    setExpandedQueries((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleReset = () => {
    setStep('input');
    setWebsiteUrl('');
    setBusinessName('');
    setDomain('');
    setCategories([]);
    setResults([]);
    setError('');
    setProgress(0);
  };

  const totalQueries = categories.reduce((sum, cat) => sum + cat.queries.filter((q) => q.query.trim()).length, 0);

  const highlightTerms = useMemo(() => {
    const terms: string[] = [];
    if (businessName) terms.push(businessName);
    if (domain) terms.push(domain);
    return terms.filter(Boolean);
  }, [businessName, domain]);

  const highlightText = (text: string) => {
    if (!highlightTerms.length || !text) return text;
    const escaped = highlightTerms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const splitRegex = new RegExp(`(${escaped.join('|')})`, 'gi');
    const matchRegex = new RegExp(`^(${escaped.join('|')})$`, 'i');
    const parts = text.split(splitRegex);
    return parts.map((part, i) => {
      if (matchRegex.test(part)) {
        return (
          <mark key={i} className="bg-[#10a37f]/20 text-[#020203] rounded px-0.5">
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  // ─── STEP 1: URL Input ───
  if (step === 'input') {
    return (
      <div className="space-y-6 mt-6">
        <div className="flex items-center gap-3">
          <img src={chatgptLogo} alt="ChatGPT" className="w-8 h-8" />
          <div>
            <h2 className="text-lg font-semibold text-[#020203]">ChatGPT Visibility Report</h2>
            <p className="text-sm text-[#020203] opacity-50 mt-1">
              Enter a website URL to generate a comprehensive ChatGPT visibility analysis
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Input
                placeholder="https://example.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleGenerateQueries()}
                className="flex-1"
              />
              <Button onClick={handleGenerateQueries} disabled={loading || !websiteUrl.trim()}>
                {loading ? 'Scraping & Generating...' : 'Generate Queries'}
              </Button>
            </div>
            {error && <p className="text-sm text-red-500 mt-3">{error}</p>}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── STEP 2: Query Edit ───
  if (step === 'edit') {
    return (
      <div className="space-y-6 mt-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[#020203]">Review & Edit Queries</h2>
            <p className="text-sm text-[#020203] opacity-50 mt-1">
              Business: <span className="font-medium text-[#020203] opacity-80">{businessName}</span> | Domain: <span className="font-mono text-[#020203] opacity-80">{domain}</span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset}>
              Back
            </Button>
            <Button onClick={handleRunReport} disabled={totalQueries === 0}>
              Run Report ({totalQueries} queries)
            </Button>
          </div>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        {categories.map((cat, catIdx) => (
          <Card key={catIdx}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-mono text-xs font-medium uppercase tracking-[0.6px] text-[#020203] opacity-50">
                  {cat.name} ({cat.queries.length})
                </h3>
                <Button variant="outline" size="sm" onClick={() => handleAddQuery(catIdx)}>
                  + Add Query
                </Button>
              </div>
              <div className="space-y-2">
                {cat.queries.map((q, qIdx) => (
                  <div key={qIdx} className="flex gap-2 items-center">
                    <Input
                      value={q.query}
                      onChange={(e) => handleUpdateQuery(catIdx, qIdx, e.target.value)}
                      placeholder="Enter query..."
                      className="flex-1 text-sm"
                    />
                    <button
                      onClick={() => handleRemoveQuery(catIdx, qIdx)}
                      className="text-[#020203] opacity-30 hover:opacity-60 transition-opacity px-2"
                      title="Remove query"
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // ─── STEP 3: Running ───
  if (step === 'running') {
    return (
      <div className="space-y-6 mt-6">
        <div className="text-center py-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src={chatgptLogo} alt="ChatGPT" className="w-8 h-8 animate-pulse" />
            <span className="text-lg font-medium text-[#020203]">Running ChatGPT queries...</span>
          </div>
          <div className="w-64 mx-auto bg-[#f3f4f5] rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-[#10a37f] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-[#020203] opacity-50 mt-3">
            Testing {totalQueries} queries through ChatGPT with web search
          </p>
          {error && <p className="text-sm text-red-500 mt-3">{error}</p>}
        </div>
      </div>
    );
  }

  // ─── STEP 4: Results ───
  const groupedResults: Record<string, ReportResultItem[]> = {};
  for (const r of results) {
    if (!groupedResults[r.category]) groupedResults[r.category] = [];
    groupedResults[r.category].push(r);
  }

  const mentionedCount = results.filter((r) => r.mentioned).length;
  const mentionRate = results.length > 0 ? Math.round((mentionedCount / results.length) * 100) : 0;

  return (
    <div className="space-y-6 mt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={chatgptLogo} alt="ChatGPT" className="w-8 h-8" />
          <div>
            <h2 className="text-lg font-semibold text-[#020203]">
              ChatGPT Visibility Report
            </h2>
            <p className="text-sm text-[#020203] opacity-50 mt-1">
              {domain} | {mentionedCount}/{results.length} queries present ({mentionRate}%)
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset}>
            New Report
          </Button>
          <Button onClick={handleDownloadPdf}>
            Download PDF
          </Button>
        </div>
      </div>

      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-[#020203]">{results.length}</p>
            <p className="text-xs font-mono uppercase tracking-wide text-[#020203] opacity-50 mt-1">Queries Tested</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className={`text-3xl font-bold ${mentionRate >= 50 ? 'text-green-600' : mentionRate >= 25 ? 'text-amber-600' : 'text-red-600'}`}>
              {mentionRate}%
            </p>
            <p className="text-xs font-mono uppercase tracking-wide text-[#020203] opacity-50 mt-1">Presence Rate</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-[#10a37f]">{mentionedCount}</p>
            <p className="text-xs font-mono uppercase tracking-wide text-[#020203] opacity-50 mt-1">Queries Present</p>
          </CardContent>
        </Card>
      </div>

      {/* Results by category */}
      {Object.entries(groupedResults).map(([category, catResults]) => {
        const catMentioned = catResults.filter((r) => r.mentioned).length;
        return (
          <div key={category} className="space-y-3">
            <div className="flex items-center gap-3">
              <img src={chatgptLogo} alt="ChatGPT" className="w-4 h-4" />
              <h3 className="font-mono text-xs font-medium uppercase tracking-[0.6px] text-[#020203] opacity-50">
                {category}
              </h3>
              <span className="text-xs text-[#020203] opacity-40">
                {catMentioned}/{catResults.length} present
              </span>
            </div>

            {catResults.map((result) => {
              const globalIdx = results.indexOf(result);
              const isExpanded = expandedQueries.has(globalIdx);
              return (
                <Card
                  key={globalIdx}
                  className="bg-white border border-[rgba(2,2,3,0.06)]"
                >
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              result.mentioned
                                ? 'bg-green-50 text-green-700'
                                : 'bg-red-50 text-red-700'
                            }`}
                          >
                            {result.mentioned ? 'Present' : 'Not Found'}
                          </span>
                          {result.urlFound && (
                            <span className="text-xs text-green-600 opacity-70">URL cited</span>
                          )}
                          {result.businessNameFound && !result.urlFound && (
                            <span className="text-xs text-amber-600 opacity-70">Name mentioned</span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-[#020203]">{result.query}</p>
                      </div>
                      <button
                        onClick={() => toggleExpanded(globalIdx)}
                        className="text-xs text-[#020203] opacity-40 hover:opacity-70 transition-opacity whitespace-nowrap"
                      >
                        {isExpanded ? 'Collapse' : 'Expand'}
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 space-y-2">
                        {result.responseText ? (
                          <div className="bg-[#f3f4f5] rounded p-3 text-sm text-[#020203] whitespace-pre-wrap leading-relaxed">
                            {highlightText(result.responseText)}
                          </div>
                        ) : (
                          <p className="text-sm text-[#020203] opacity-40 italic">No response</p>
                        )}

                        {result.citations.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs font-mono uppercase tracking-wide text-[#020203] opacity-40">
                              Citations ({result.citations.length})
                            </p>
                            {result.citations.map((cit, cIdx) => (
                              <a
                                key={cIdx}
                                href={cit.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`block text-xs truncate ${
                                  cit.url.toLowerCase().includes(domain)
                                    ? 'text-green-600 font-medium'
                                    : 'text-blue-500'
                                } hover:underline`}
                              >
                                {cit.title || cit.url}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
